import { MimeTypes } from "#/components/utls/utils";
import { Database } from "../supabase/supabaseSchema";
import { MedicalRecordType } from "./diagnosis.type";

export type MessageFileStore = {
  id: string;
  message_id: string;
  file_store_id: string;
  file_store: FileStore;
};

export type FileStore = Database["public"]["Tables"]["file_store"]["Row"];

export type FileMediaCategory =
  | "image"
  | "audio"
  | "video"
  | "pdf"
  | "document"
  | "archive"
  | "other";

export interface LinkedMedicalRecord<Entity = Record<string, unknown>> {
  entityType: MedicalRecordType | string;
  entityId: string;
  linkedAt: string;
  messageId?: string | null;
  entity?: Entity | null;
}
export interface Image {
  url: string;
  fileName: string;
  fileType: MimeTypes;
}

export interface FileStoreWithMedicalRecords {
  file: (FileStore & { signed_url?: string | null }) | FileStore;
  medicalRecords: LinkedMedicalRecord[];
}

export interface FileStorePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface GalleryState {
  images: MedicalRecordAwareFileStore[];
  isLoading: boolean;
  error: Error | null;
  currentIndex: number;
}

export interface FileStoreListResponse {
  data: FileStoreWithMedicalRecords[];
  pagination: FileStorePagination;
  error: null;
  expires: number;
}

export interface FileStoreDetailResponse {
  data: FileStoreWithMedicalRecords;
  error: null;
  expires: number;
}

export type MedicalRecordAwareFileStore = FileStore & {
  signed_url?: string | null;
  medicalRecords: LinkedMedicalRecord[];
  mediaCategory: FileMediaCategory;
  fileExtension?: string | null;
};

type MaybeWrappedFile =
  | FileStoreWithMedicalRecords
  | (FileStore & {
      signed_url?: string | null;
      signedUrl?: string | null;
      medicalRecords?: LinkedMedicalRecord[];
      linkedMedicalRecords?: LinkedMedicalRecord[];
    });

const isWrappedFileStore = (
  value: MaybeWrappedFile
): value is FileStoreWithMedicalRecords => {
  return Boolean(value && typeof value === "object" && "file" in value);
};

const mimeCategoryFromType = (type?: string | null): FileMediaCategory => {
  if (!type) return "other";
  const lower = type.toLowerCase();
  if (lower.startsWith("image/")) return "image";
  if (lower.startsWith("audio/")) return "audio";
  if (lower.startsWith("video/")) return "video";
  if (lower === "application/pdf") return "pdf";
  if (
    lower === "application/msword" ||
    lower ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    lower === "application/vnd.ms-powerpoint" ||
    lower ===
      "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
    lower === "application/vnd.ms-excel" ||
    lower ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    lower.startsWith("text/")
  )
    return "document";
  if (
    lower === "application/zip" ||
    lower === "application/x-zip-compressed" ||
    lower === "application/x-tar" ||
    lower === "application/x-7z-compressed"
  )
    return "archive";
  return "other";
};

const extensionFromName = (name?: string | null): string | null => {
  if (!name) return null;
  const match = /\.([^.]+)$/.exec(name);
  return match ? match[1].toLowerCase() : null;
};

const deriveCategoryFromExtension = (
  extension?: string | null
): FileMediaCategory => {
  if (!extension) return "other";
  switch (extension) {
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "bmp":
    case "webp":
    case "heic":
    case "svg":
      return "image";
    case "mp3":
    case "wav":
    case "ogg":
    case "m4a":
    case "aac":
      return "audio";
    case "mp4":
    case "mov":
    case "avi":
    case "mkv":
    case "webm":
      return "video";
    case "pdf":
      return "pdf";
    case "doc":
    case "docx":
    case "ppt":
    case "pptx":
    case "xls":
    case "xlsx":
    case "txt":
    case "md":
      return "document";
    case "zip":
    case "rar":
    case "7z":
    case "tar":
    case "gz":
      return "archive";
    default:
      return "other";
  }
};

export const normalizeFileStoreWithMedicalRecords = (
  entry: MaybeWrappedFile
): MedicalRecordAwareFileStore => {
  const file = isWrappedFileStore(entry) ? entry.file : entry;

  const rawRecords = isWrappedFileStore(entry)
    ? entry.medicalRecords
    : entry.medicalRecords ?? entry.linkedMedicalRecords;

  const signedUrl =
    (file as { signed_url?: string | null; signedUrl?: string | null })
      .signed_url ??
    (file as { signed_url?: string | null; signedUrl?: string | null })
      .signedUrl ??
    null;

  const extension = extensionFromName(file?.name);
  const mediaCategory = (() => {
    const fromMime = mimeCategoryFromType(file?.type);
    if (fromMime !== "other") return fromMime;
    return deriveCategoryFromExtension(extension);
  })();

  return {
    ...(file as FileStore),
    signed_url: signedUrl ?? null,
    medicalRecords: Array.isArray(rawRecords) ? rawRecords : [],
    mediaCategory,
    fileExtension: extension,
  } as MedicalRecordAwareFileStore;
};
